import { Building2, Eye, Lock } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { StarButton } from "@/components/ui/StarButton";
import { cn } from "@/lib/utils";
import { stageMeta } from "@/lib/domain-constants";
import { capture, contractingProps } from "@/lib/posthog";
import { formatMoney, formatDate, formatMonth } from "@/lib/format";
import {
  translateSubject,
  subjectClass,
  stageClass,
  NewBadge,
  MinorBadge,
  RegistrationEnd,
  FROM_BASES_HINT,
} from "./pills";
import { MatchList } from "./MatchPill";
import { QualificationBadge } from "./QualificationBadge";
import { ContractingCard } from "./ContractingCard";
import { StatusActions } from "./StatusActions";
import { AssigneeSelect } from "@/components/team/AssigneeSelect";
import { useContractingRow } from "./useContractingRow";
import {
  GHOST_NOMENCLATURES,
  GHOST_DESCRIPTIONS,
  GHOST_LOCATIONS,
  ghostPick,
} from "@/lib/ghost";
import type { ContractingItem } from "@/types/domain";

// Columns: Licitación · Ubicación · Fecha publicación · Objeto · Etapa · Monto·Match · acciones
// La primera columna es minmax(0,1fr): se encoge con el contenedor en vez de
// forzar scroll horizontal (el resto son anchos fijos y ajustados al contenido).
const GRID_COLS = "minmax(0,1fr) 130px 90px 80px 130px 240px 70px";

// En el pipeline hacen falta dos columnas más — en qué estado está y quién la
// lleva — que en el descubrimiento no significan nada.
const CRM_GRID_COLS =
  "minmax(0,1fr) 120px 90px 130px 130px 240px 56px 70px";

function Row({
  c,
  onView,
  showTracking = false,
}: {
  c: ContractingItem;
  onView?: (c: ContractingItem) => void;
  showTracking?: boolean;
}) {
  const {
    locked,
    currencySymbol,
    amount,
    matches,
    stage,
    daysContratado: days,
    registrationEndsAt,
    saved,
    untrackedCount,
    hasUntracked,
    starDisabled,
    starTitle,
    onStarClick,
  } = useContractingRow(c);

  return (
    <div
      onClick={() => onView?.(c)}
      style={{ gridTemplateColumns: showTracking ? CRM_GRID_COLS : GRID_COLS }}
      className="grid cursor-pointer items-center gap-4 border-b px-6 py-3 text-sm transition-colors last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 md:px-8"
    >
      {/* Licitación */}
      {locked ? (
        <div className="flex min-w-0 items-center gap-3">
          {/* <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
            <Lock className="size-4" />
          </span> */}
          {c.isNew && <NewBadge />}
          {c.contracting_minorid && <MinorBadge />}
          {/* Data fantasma: texto plausible difuminado. select-none + aria-hidden
              para que no sea seleccionable ni leído por lectores de pantalla. */}
          <div className="flex min-w-0 select-none flex-col" aria-hidden="true">
            <div className="truncate text-[11px] font-bold text-slate-400 blur-[4px]">
              {ghostPick(c.contracting_id, GHOST_NOMENCLATURES)}
            </div>
            <div className="mt-1 line-clamp-2 text-[11px] text-slate-300 blur-[4px]">
              {ghostPick(c.contracting_id, GHOST_DESCRIPTIONS)}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            {c.isNew && <NewBadge />}
            {c.contracting_minorid && <MinorBadge />}
            <div className="truncate text-[11px] font-bold">
              {c.contracting_nomenclature}
              {c.contracting_entity ? ` · ${c.contracting_entity}` : ""}
            </div>
          </div>
          <div
            className="mt-1 line-clamp-2 text-[11px] text-slate-400"
            title={c.contracting_description}
          >
            {c.contracting_description}
          </div>
        </div>
      )}

      {/* Ubicación: departamento · provincia · distrito. Provincia y distrito son
          data bloqueada (el backend las anula): se muestran difuminadas. */}
      <div className="flex min-w-0 flex-col text-slate-600 dark:text-slate-300">
        <div className="truncate font-medium text-[11px]">
          {c.department?.department_name || "-"}
        </div>
        {locked ? (
          <div
            className="truncate select-none text-[11px] text-slate-400 blur-[3px]"
            aria-hidden="true"
          >
            {ghostPick(c.contracting_id, GHOST_LOCATIONS)}
          </div>
        ) : (
          (c.province?.province_name || c.district?.district_name) && (
            <div className="truncate text-[11px] text-slate-400">
              {[c.province?.province_name, c.district?.district_name]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )
        )}
      </div>

      {/* Fecha de publicación. Bloqueada va sin día: con la fecha exacta se
          filtra el SEACE y se identifica la licitación sin gastar la vista. */}
      <div className="truncate text-slate-600 dark:text-slate-300 text-[12px]">
        {locked
          ? formatMonth(c.contracting_publicatedat)
          : formatDate(c.contracting_publicatedat)}
      </div>

      {/* Objeto, o el estado del pipeline cuando esta tabla es el seguimiento. */}
      {showTracking ? (
        <div className="flex items-center">
          <StatusActions
            contractingId={c.contracting_id}
            status={c.contractinguser?.contractinguser_status}
          />
        </div>
      ) : (
        <div className="flex items-center">
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              subjectClass(c.contracting_contractsubject),
            )}
          >
            {translateSubject(c.contracting_contractsubject)}
          </span>
        </div>
      )}

      {/* Etapa */}
      <div className="flex flex-col items-start gap-1">
        <span
          className={cn(
            "inline-flex items-center gap-2 truncate rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase leading-tight tracking-wider",
            stageClass(stage),
          )}
          title={stageMeta(stage).label}
        >
          {stageMeta(stage).label}
          {days !== null && (
            <span className="font-semibold normal-case tracking-normal opacity-80">
              {days}d
            </span>
          )}
        </span>
        {registrationEndsAt && <RegistrationEnd endsAt={registrationEndsAt} />}
      </div>

      {/* Monto · Oportunidad — el monto total se difumina cuando está bloqueada;
          las coincidencias se muestran igual (bloqueada o no). */}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-baseline gap-1.5 justify-end">
          <span
            className="shrink-0 text-[10px] text-slate-400"
            title={amount.fromBases ? FROM_BASES_HINT : undefined}
          >
            Valor referencial{amount.fromBases && " · bases"}
          </span>
          {locked ? (
            <span
              className="select-none font-bold tabular-nums text-slate-400 blur-[5px]"
              aria-hidden="true"
            >
              {currencySymbol} 0,000,000
            </span>
          ) : (
            <span className="truncate font-bold tabular-nums">
              {amount.amount === null
                ? "-"
                : formatMoney(amount.amount, currencySymbol)}
            </span>
          )}
        </div>
        {c.qualification && (
          <QualificationBadge qualification={c.qualification} className="self-end" />
        )}
        <MatchList
          matches={matches}
          currencySymbol={currencySymbol}
          isMinor={!!c.contracting_minorid}
        />
      </div>

      {/* Responsable: solo en el pipeline. */}
      {showTracking && (
        <div className="flex items-center justify-center">
          <AssigneeSelect
            contractingId={c.contracting_id}
            assignedUserId={c.contractinguser?.assignedUserId}
            compact
          />
        </div>
      )}

      {/* Acciones */}
      {locked ? (
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(c);
            }}
            title="Desbloquear oportunidad (1 vista)"
            className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11px] font-bold text-primary transition-colors hover:bg-primary/20 cursor-pointer"
          >
            <Lock className="size-3.5" />
            Ver
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <StarButton
            saved={saved}
            onClick={onStarClick}
            disabled={starDisabled}
            title={starTitle}
            badge={hasUntracked ? untrackedCount : undefined}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(c);
            }}
            title="Ver detalle"
            className="flex size-8 items-center justify-center rounded-full border bg-slate-50 text-slate-600 transition-colors hover:bg-slate-30 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 cursor-pointer"
          >
            <Eye className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export function ContractingTable({
  items,
  isLoading,
  onRowClick,
  emptyLabel = "No se encontraron licitaciones con estos filtros.",
  showTracking = false,
}: {
  items: ContractingItem[];
  isLoading?: boolean;
  onRowClick?: (item: ContractingItem) => void;
  emptyLabel?: string;
  /** En el pipeline se muestran el estado y el responsable en vez del objeto. */
  showTracking?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <Building2 className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }

  function openDetail(item: ContractingItem) {
    capture("contracting_detail_opened", contractingProps(item));
    onRowClick?.(item);
  }

  return (
    <>
      {/* Móvil y tablet: tarjetas. La tabla pide 960px de ancho, que ahí no hay. */}
      <div className="flex flex-col gap-3 lg:hidden">
        {items.map((c) => (
          <ContractingCard key={c.contracting_id} c={c} onView={openDetail} />
        ))}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <div className={showTracking ? "min-w-[1120px]" : "min-w-[960px]"}>
          {/* Header */}
          <div
            style={{
              gridTemplateColumns: showTracking ? CRM_GRID_COLS : GRID_COLS,
            }}
            className="grid gap-4 border-b bg-slate-50 px-6 py-4 text-sm font-semibold text-slate-600 dark:bg-white/5 dark:text-slate-300 md:px-8"
          >
            <div className="truncate">Licitación</div>
            <div className="truncate">Ubicación</div>
            <div className="truncate">Publicación</div>
            <div className="truncate">{showTracking ? "Estado" : "Objeto"}</div>
            <div className="truncate">Etapa</div>
            <div className="truncate text-emerald-600 text-right">
              Monto · Oportunidad
            </div>
            {showTracking && (
              <div className="truncate text-center">Lleva</div>
            )}
            <div className="truncate" />
          </div>
          {/* Rows */}
          {items.map((c) => (
            <Row
              key={c.contracting_id}
              c={c}
              onView={openDetail}
              showTracking={showTracking}
            />
          ))}
        </div>
      </div>
    </>
  );
}
